/**
 * Rax Theme - Yellow/Black Color Scheme
 *
 * Import and spread this into your component styles for consistent theming.
 */

import { css } from "lit";

export const raxTheme = css`
  :host {
    /* Rax Brand Colors */
    --rax-yellow: #ffd000;
    --rax-gold: #ffe135;
    --rax-amber: #ffa500;
    --rax-black: #0a0a0a;
    --rax-dark: #111111;
    --rax-charcoal: #1a1a1a;
  
    /* Semantic Colors */
    --bg-primary: var(--rax-black);
    --bg-secondary: var(--rax-dark);
    --bg-elevated: var(--rax-charcoal);
    --bg-subtle: #222222;
    --bg-hover: #2a2a2a;
  
    --text-primary: #ffffff;
    --text-secondary: #aaaaaa;
    --text-tertiary: #666666;
    --text-accent: var(--rax-yellow);
  
    --border-color: #2a2a2a;
    --border-hover: var(--rax-yellow);
  
    --accent: var(--rax-yellow);
    --accent-light: var(--rax-gold);
    --accent-glow: rgba(255, 208, 0, 0.3);
  
    --success: #10b981;
    --warning: #f59e0b;
    --error: #ef4444;
    --info: #3b82f6;
  
    --link-color: var(--rax-yellow);
  
    /* Shadows */
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.5);
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.5);
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.5);
    --shadow-glow: 0 0 20px var(--accent-glow);
  
    /* Transitions */
    --transition-fast: 0.15s ease;
    --transition-normal: 0.2s ease;
    --transition-slow: 0.3s ease;
  
    /* Border Radius */
    --radius-sm: 4px;
    --radius-md: 8px;
    --radius-lg: 12px;
    --radius-xl: 16px;
    --radius-full: 9999px;
  
    /* Font Sizes */
    --font-xs: 0.75rem;
    --font-sm: 0.875rem;
    --font-md: 1rem;
    --font-lg: 1.125rem;
    --font-xl: 1.25rem;
    --font-2xl: 1.5rem;
  
    /* Spacing */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
  }
`;

/**
 * Common button styles for Rax components
 */
export const raxButtonStyles = css`
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-weight: 600;
    font-size: var(--font-sm);
    cursor: pointer;
    transition: all var(--transition-fast);
    border: none;
    text-decoration: none;
  }
  
  .btn-primary {
    background: var(--rax-yellow);
    color: var(--rax-black);
  }
  
  .btn-primary:hover {
    background: var(--rax-gold);
    box-shadow: var(--shadow-glow);
  }
  
  .btn-secondary {
    background: transparent;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }
  
  .btn-secondary:hover {
    border-color: var(--rax-yellow);
    color: var(--rax-yellow);
  }
  
  .btn-ghost {
    background: transparent;
    color: var(--text-secondary);
  }
  
  .btn-ghost:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  
  .btn-danger {
    background: var(--error);
    color: white;
  }
  
  .btn-danger:hover {
    filter: brightness(1.1);
  }
  
  .btn-sm {
    padding: var(--space-2) var(--space-3);
    font-size: var(--font-xs);
  }
  
  .btn-lg {
    padding: var(--space-4) var(--space-6);
    font-size: var(--font-md);
  }
  
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Common card styles for Rax components
 */
export const raxCardStyles = css`
  .card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    padding: var(--space-4);
    transition: all var(--transition-fast);
  }
  
  .card:hover {
    border-color: var(--rax-yellow);
  }
  
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-4);
    padding-bottom: var(--space-3);
    border-bottom: 1px solid var(--border-color);
  }
  
  .card-title {
    font-size: var(--font-lg);
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .card-subtitle {
    font-size: var(--font-sm);
    color: var(--text-secondary);
  }
`;

/**
 * Common badge styles
 */
export const raxBadgeStyles = css`
  .badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-full);
    font-size: var(--font-xs);
    font-weight: 500;
  }
  
  .badge-success {
    background: rgba(16, 185, 129, 0.2);
    color: var(--success);
  }
  
  .badge-warning {
    background: rgba(245, 158, 11, 0.2);
    color: var(--warning);
  }
  
  .badge-error {
    background: rgba(239, 68, 68, 0.2);
    color: var(--error);
  }
  
  .badge-info {
    background: rgba(59, 130, 246, 0.2);
    color: var(--info);
  }
  
  .badge-accent {
    background: var(--accent-glow);
    color: var(--rax-yellow);
  }
`;

/**
 * Common input styles
 */
export const raxInputStyles = css`
  .input {
    width: 100%;
    padding: var(--space-3) var(--space-4);
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: var(--font-sm);
    transition: all var(--transition-fast);
  }
  
  .input:focus {
    outline: none;
    border-color: var(--rax-yellow);
    box-shadow: 0 0 0 3px var(--accent-glow);
  }
  
  .input::placeholder {
    color: var(--text-tertiary);
  }
  
  .input-label {
    display: block;
    margin-bottom: var(--space-2);
    font-size: var(--font-sm);
    font-weight: 500;
    color: var(--text-secondary);
  }
  
  .input-error {
    border-color: var(--error);
  }
  
  .input-error:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3);
  }
`;
